import { Children, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { SpringGrid, layout, measureItems, makeResponsive } from "react-stonecutter";

import { useResponsiveFlag } from "../../../Utils/ResponsiveQueries";

const MOBILE_CONTAINER_MAX_WIDTH = 540;

const BaseGrid = measureItems(SpringGrid, { measureImages: true });
const DesktopGrid = makeResponsive(BaseGrid, { maxWidth: 756, minPadding: 0 });
const NonDesktopGrid = makeResponsive(BaseGrid, {
  maxWidth: MOBILE_CONTAINER_MAX_WIDTH,
  minPadding: 0
});

const COLUMN_WIDTH_LARGE_DESTKOP = 372;
const COLUMN_WIDTH_SMALL_DESTKOP = 312;
const COLUMN_WIDTH_TABLET = 232;
const COLUMN_WIDTH_MOBILE = 540;

const GRID_GUTTER_WIDTH = 12;
// The greater gutterHeight leaves the space for the contribution boxes top labels that
// are 15px upper than the border of their containing white box.
const GRID_GUTTER_HEIGHT = 23;

// This component allows rendering a grid of datavization contributions arranged in a
// masonry style display, thanks to the `react-stonecutter` package which handles the
// underlying system.
//
const ContributionsDatavizListContributionsGrid = props => {
  const { children } = props;

  const [container, setContainer] = useState();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { isDesktop, isLargeDesktop, isSmallDesktop, isTablet } = useResponsiveFlag();
  const iFrameMode = useSelector(({ styling }) => styling.iFrameMode);

  const [Grid, defaultColumnWidth, fullWidth] = useMemo(() => {
    if (iFrameMode && isDesktop && container) {
      const width = container.clientWidth / 2 - 6;
      return [DesktopGrid, width, false];
    } else if (isLargeDesktop) {
      return [DesktopGrid, COLUMN_WIDTH_LARGE_DESTKOP, false];
    } else if (isSmallDesktop) {
      return [DesktopGrid, COLUMN_WIDTH_SMALL_DESTKOP, false];
    } else if (isTablet) {
      return [NonDesktopGrid, COLUMN_WIDTH_TABLET, false];
    } else {
      return [NonDesktopGrid, COLUMN_WIDTH_MOBILE, true];
    }
  }, [container, iFrameMode, isDesktop, isLargeDesktop, isSmallDesktop, isTablet]);

  // Handle the case of mobile display where there's only one column that takes the whole
  // window space. Note that in mobile display, the bootstrap's `.container` can only be
  // of a maximum of 540px.
  const columnWidth = fullWidth
    ? Math.min(MOBILE_CONTAINER_MAX_WIDTH, windowWidth - 20)
    : defaultColumnWidth;

  useEffect(() => {
    const handleWindowResize = () => {
      const { innerWidth } = window;
      if (windowWidth != innerWidth) setWindowWidth(innerWidth);
    };

    window.removeEventListener("resize", handleWindowResize);
    window.addEventListener("resize", handleWindowResize);

    // Cleanup event listener on component unmounting
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [windowWidth]);

  return (
    <div
      className="participative-datavisualization-contributions-list margin-bottom-md"
      ref={setContainer}
    >
      <Grid
        component="div"
        columnWidth={columnWidth}
        gutterWidth={GRID_GUTTER_WIDTH}
        gutterHeight={GRID_GUTTER_HEIGHT}
        layout={layout.pinterest}
        duration={400}
        easing="ease-out"
      >
        {Children.map(children, child => (
          <div style={{ width: columnWidth }}>{child}</div>
        ))}
      </Grid>
    </div>
  );
};

export default ContributionsDatavizListContributionsGrid;
